import { useState, useMemo } from 'react'
import { X, Copy, CheckCircle, ZoomIn, Download, Pencil, GitBranch, ImageIcon, ArrowRightLeft, MessageSquareDot, Scissors, Loader2, FileDown } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import type { GeneratedImage } from '../../lib/api'
import { getImageAssetUrl } from '../../lib/api'
import { ExportModal } from './ExportModal'

interface ImageGalleryProps {
  images: GeneratedImage[]
  /** When provided, shows "Editar con IA" in the lightbox footer. */
  onRequestEdit?: (image: GeneratedImage) => void
  /** When provided, shows "Usar como referencia" to start a new generation from this image. */
  onUseAsReference?: (image: GeneratedImage) => void
  /** When provided, shows "Eliminar fondo" in the lightbox footer. */
  onRemoveBackground?: (image: GeneratedImage) => Promise<void>
}

type Session = { visualTaskId: string; createdAt: string; images: GeneratedImage[] }

function groupImagesBySession(images: GeneratedImage[]): Session[] {
  const byTask = new Map<string, GeneratedImage[]>()
  for (const img of images) {
    const list = byTask.get(img.visual_task_id) || []
    list.push(img)
    byTask.set(img.visual_task_id, list)
  }
  return Array.from(byTask.entries())
    .map(([visualTaskId, imgs]) => {
      const sorted = [...imgs].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      const createdAt = sorted[0]?.created_at ?? ''
      return { visualTaskId, createdAt, images: sorted }
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

export function ImageGallery({ images, onRequestEdit, onUseAsReference, onRemoveBackground }: ImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null)
  const [compareMode, setCompareMode] = useState<'single' | 'before_after'>('single')
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null)
  const [removingBgId, setRemovingBgId] = useState<string | null>(null)
  const [exportModalImageId, setExportModalImageId] = useState<string | null>(null)

  const sessions = useMemo(() => groupImagesBySession(images), [images])

  /** Set of image IDs that have been used as a source for at least one edit */
  const editedSourceIds = useMemo(
    () => new Set(images.map((img) => img.source_image_id).filter(Boolean) as string[]),
    [images]
  )

  const copyPrompt = async (prompt: string, imageId: string) => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopiedPrompt(imageId)
      setTimeout(() => setCopiedPrompt(null), 2000)
    } catch (err) {
      console.error('Failed to copy prompt:', err)
    }
  }

  const downloadImage = async (imageUrl: string, imageId: string) => {
    try {
      const fetchUrl = imageId ? getImageAssetUrl(imageId) : imageUrl
      const response = await fetch(fetchUrl)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `image-${imageId}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Failed to download image:', err)
    }
  }

  const handleRemoveBackground = async (image: GeneratedImage) => {
    if (!onRemoveBackground || removingBgId) return
    setRemovingBgId(image.id)
    try {
      await onRemoveBackground(image)
      setSelectedImage(null)
    } finally {
      setRemovingBgId(null)
    }
  }

  const openLightbox = (image: GeneratedImage) => {
    setSelectedImage(image)
    setCompareMode(image.source_image_id ? 'before_after' : 'single')
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>No hay imagenes generadas</p>
      </div>
    )
  }

  return (
    <>
      {/* Export Modal */}
      {exportModalImageId && (
        <ExportModal
          isOpen={!!exportModalImageId}
          onClose={() => setExportModalImageId(null)}
          imageId={exportModalImageId}
          imageLabel={`image-${exportModalImageId.slice(0, 8)}`}
        />
      )}

      {/* Timeline by session */}
      <div className="space-y-6">
        {sessions.map((session) => (
          <div key={session.visualTaskId}>
            <div className="flex items-center gap-2 mb-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <span>Sesion</span>
              <span>
                {format(new Date(session.createdAt), 'd MMM yyyy, HH:mm', { locale: es })}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {session.images.map((image) => (
                <div
                  key={image.id}
                  className="group relative rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => openLightbox(image)}
                >
                  <div className="aspect-square bg-slate-100 relative">
                    {image.id ? (
                      <img
                        src={getImageAssetUrl(image.id)}
                        alt="Generated visual"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (image.public_url) {
                            ;(e.target as HTMLImageElement).src = image.public_url
                          }
                        }}
                      />
                    ) : image.public_url ? (
                      <img
                        src={image.public_url}
                        alt="Generated visual"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <span className="text-sm">No disponible</span>
                      </div>
                    )}
                    {image.source_image_id && (
                      <div
                        className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-800 text-xs font-medium"
                        title="Editada desde otra imagen"
                      >
                        <GitBranch className="w-3 h-3" />
                        Editada
                      </div>
                    )}
                    {editedSourceIds.has(image.id) && (
                      <div
                        className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-xs font-medium"
                        title="Esta imagen tiene ediciones activas"
                      >
                        <MessageSquareDot className="w-3 h-3" />
                        Sesión
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-3 bg-white">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                      <span>{image.model_used}</span>
                      {image.width && image.height && (
                        <span>{image.width}x{image.height}</span>
                      )}
                    </div>
                    {image.prompt_used && (
                      <p className="text-xs text-slate-600 line-clamp-2">
                        {image.prompt_used}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-6xl w-full max-h-[90vh] bg-white rounded-lg overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>

            {/* Compare mode toggle when image has source */}
            {selectedImage.source_image_id && (
              <div className="absolute top-4 left-4 z-10 flex gap-1 p-1 bg-white rounded-lg shadow-lg">
                <button
                  type="button"
                  onClick={() => setCompareMode('single')}
                  className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 ${
                    compareMode === 'single'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ImageIcon className="w-4 h-4" />
                  Actual
                </button>
                <button
                  type="button"
                  onClick={() => setCompareMode('before_after')}
                  className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 ${
                    compareMode === 'before_after'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Antes / Despues
                </button>
              </div>
            )}

            <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center min-h-[200px]">
              {compareMode === 'before_after' && selectedImage.source_image_id ? (
                <div className="grid grid-cols-2 gap-4 p-4 w-full max-w-5xl">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Antes
                    </p>
                    <img
                      src={getImageAssetUrl(selectedImage.source_image_id)}
                      alt="Original"
                      className="w-full h-auto rounded border border-slate-200 object-contain max-h-[60vh]"
                      onError={(e) => {
                        const sourceImg = images.find((i) => i.id === selectedImage.source_image_id)
                        if (sourceImg?.public_url) {
                          ;(e.target as HTMLImageElement).src = sourceImg.public_url
                        }
                      }}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Despues
                    </p>
                    {selectedImage.id ? (
                      <img
                        src={getImageAssetUrl(selectedImage.id)}
                        alt="Editada"
                        className="w-full h-auto rounded border border-slate-200 object-contain max-h-[60vh]"
                        onError={(e) => {
                          if (selectedImage.public_url) {
                            ;(e.target as HTMLImageElement).src = selectedImage.public_url
                          }
                        }}
                      />
                    ) : selectedImage.public_url ? (
                      <img
                        src={selectedImage.public_url}
                        alt="Editada"
                        className="w-full h-auto rounded border border-slate-200 object-contain max-h-[60vh]"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-48 text-slate-400">
                        No disponible
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-h-[70vh] overflow-auto flex items-center justify-center p-4">
                  {selectedImage.id ? (
                    <img
                      src={getImageAssetUrl(selectedImage.id)}
                      alt="Generated visual"
                      className="max-w-full h-auto"
                      onError={(e) => {
                        if (selectedImage.public_url) {
                          ;(e.target as HTMLImageElement).src = selectedImage.public_url
                        }
                      }}
                    />
                  ) : selectedImage.public_url ? (
                    <img
                      src={selectedImage.public_url}
                      alt="Generated visual"
                      className="max-w-full h-auto"
                    />
                  ) : (
                    <div className="py-12 text-slate-400">
                      <p>Imagen no disponible</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 bg-white border-t border-slate-200">
              <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
                <span className="font-medium">{selectedImage.model_used}</span>
                {selectedImage.width && selectedImage.height && (
                  <span>{selectedImage.width}x{selectedImage.height}</span>
                )}
                {selectedImage.source_image_id && (
                  <span className="flex items-center gap-1 text-amber-700">
                    <GitBranch className="w-4 h-4" />
                    Editada
                  </span>
                )}
              </div>

              {selectedImage.prompt_used && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Prompt:</span>
                    <button
                      onClick={() => copyPrompt(selectedImage.prompt_used, selectedImage.id)}
                      className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {copiedPrompt === selectedImage.id ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">
                    {selectedImage.prompt_used}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {(selectedImage.id || selectedImage.public_url) && (
                  <>
                    <button
                      onClick={() =>
                        downloadImage(
                          selectedImage.id
                            ? getImageAssetUrl(selectedImage.id)
                            : selectedImage.public_url ?? '',
                          selectedImage.id
                        )
                      }
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Descargar
                    </button>
                    {selectedImage.id && (
                      <button
                        onClick={() => setExportModalImageId(selectedImage.id)}
                        className="btn btn-secondary flex items-center gap-2"
                      >
                        <FileDown className="w-4 h-4" />
                        Exportar
                      </button>
                    )}
                  </>
                )}
                {onRequestEdit && (
                  <button
                    onClick={() => onRequestEdit(selectedImage)}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Editar con IA
                  </button>
                )}
                {onUseAsReference && (
                  <button
                    onClick={() => {
                      onUseAsReference(selectedImage)
                      setSelectedImage(null)
                    }}
                    className="btn btn-secondary flex items-center gap-2 border-amber-200 text-amber-800 hover:bg-amber-50"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Usar como referencia
                  </button>
                )}
                {onRemoveBackground && (
                  <button
                    onClick={() => handleRemoveBackground(selectedImage)}
                    disabled={removingBgId === selectedImage.id}
                    className="btn btn-secondary flex items-center gap-2 border-violet-200 text-violet-800 hover:bg-violet-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {removingBgId === selectedImage.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Scissors className="w-4 h-4" />
                    )}
                    Eliminar fondo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
